import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { useCan } from '../../contexts/PermissionsContext'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { ListingCreateDialog } from './ListingCreateDialog'
import { ListingStatusBadge } from './listingShared'

/** On a lot: its listings, and the way to advertise it (spec 2026-10-25-10). */
export function AssetListingCard({ org, assetUid, assetName }: { org: string; assetUid: string; assetName?: string }) {
    const { t } = useTranslation('listings')
    const canRead = useCan('listings.read')
    const canWrite = useCan('listings.write')
    const [creating, setCreating] = useState(false)
    const { data } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/listings',
        { params: { path: { orgCode: org }, query: { asset_uid: assetUid } } },
        { enabled: canRead },
    )
    if (!canRead) return null
    const rows = (data?.listings ?? []).filter((l) => l.status !== 'withdrawn')
    const live = rows.some((l) => l.status === 'draft' || l.status === 'published' || l.status === 'paused')
    return (
        <Card data-testid="asset-listings-card">
            <CardHeader className="pb-2"><CardTitle className="text-base">{t('title')}</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
                {rows.map((l) => (
                    <div key={l.uid} className="flex items-center gap-2">
                        <ListingStatusBadge status={l.status} />
                        <Link to="/$org/listings/$listingUid" params={{ org, listingUid: l.uid }} className="hover:underline" data-testid="asset-listing-link">
                            {t('editor.open_listing')}
                        </Link>
                    </div>
                ))}
                {canWrite && !live && (
                    <Button size="sm" onClick={() => setCreating(true)} data-testid="asset-create-listing-button">{t('create.button')}</Button>
                )}
                <ListingCreateDialog org={org} open={creating} onOpenChange={setCreating} assetUid={assetUid} assetName={assetName} />
            </CardContent>
        </Card>
    )
}
